/**
 * ClientsPage Component (TASK-337, tabs container; clients list since TASK-137)
 *
 * URL-driven tabs container of the Clients section (FR-001):
 *   /clients            → tab Контакты            (ClientsListView)
 *   /clients/cold-calls → tab «Холодные обзвоны» (ColdCallsView, former DatasetsPage)
 *
 * The active tab is derived from the URL — a direct link / reload keeps it —
 * and switching a tab navigates (routing, not local state). Only the active
 * view is mounted, so the header counter always reflects the active tab.
 * KAN-811: the cold-calls tab is closed to the MANAGER role; with one tab left
 * the tabs bar is not shown.
 */
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCanOpenPath } from '@/hooks/useApiAccess';
import { ClientsListView } from './ClientsListView';
import { ColdCallsView } from './ColdCallsView';

const TAB_ROUTES: Record<string, string> = {
  clients: '/clients',
  'cold-calls': '/clients/cold-calls',
};

export function ClientsPage(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();

  const activeTab = location.pathname.startsWith('/clients/cold-calls')
    ? 'cold-calls'
    : 'clients';

  const handleTabChange = useCallback(
    (value: string) => {
      const target = TAB_ROUTES[value];
      if (target && target !== location.pathname) {
        navigate(target);
      }
    },
    [navigate, location.pathname]
  );

  const coldCallsAllowed = useCanOpenPath(TAB_ROUTES['cold-calls']);
  const tabsBar = coldCallsAllowed ? (
    <TabsList className="mb-4">
      <TabsTrigger value="clients">Контакты</TabsTrigger>
      <TabsTrigger value="cold-calls">Холодные обзвоны</TabsTrigger>
    </TabsList>
  ) : null;

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange}>
      {activeTab === 'cold-calls' ? (
        <ColdCallsView tabsBar={tabsBar} />
      ) : (
        <ClientsListView tabsBar={tabsBar} />
      )}
    </Tabs>
  );
}

export default ClientsPage;
